// src/pages/VerifyEmail.jsx
import React, { useEffect, useState, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  verify_email,
  resend_verification,
  messageClear,
} from "../store/reducers/authReducer";
import { AiOutlineMail } from "react-icons/ai";
import toast from "react-hot-toast";
import FadeLoader from "react-spinners/FadeLoader";
import Headers from "../components/Headers";
import Footer from "../components/Footer";
import { LanguageContext } from "../context/LanguageContext";

const VerifyEmail = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loader, successMessage, errorMessage, userInfo } = useSelector(
    (state) => state.auth
  );
  const { t } = useContext(LanguageContext);
  const [countdown, setCountdown] = useState(0);
  const [otp, setOtp] = useState({
    digit1: "",
    digit2: "",
    digit3: "",
    digit4: "",
    digit5: "",
    digit6: "",
  });

  useEffect(() => {
    if (!userInfo) {
      navigate("/login");
    } else if (userInfo.verified) {
      navigate("/");
    }
  }, [userInfo]);

  useEffect(() => {
    if (successMessage) {
      toast.success(successMessage);
      dispatch(messageClear());
    }
    if (errorMessage) {
      toast.error(errorMessage);
      dispatch(messageClear());
    }
  }, [successMessage, errorMessage]);

  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  const otpHandle = (e) => {
    const { name, value } = e.target;
    if (/^\d*$/.test(value) && value.length <= 1) {
      setOtp({
        ...otp,
        [name]: value,
      });

      if (value && name !== "digit6") {
        const nextInput = document.querySelector(
          `input[name=digit${parseInt(name.replace("digit", "")) + 1}]`
        );
        if (nextInput) nextInput.focus();
      }
    }
  };

  const verifyEmail = (e) => {
    e.preventDefault();
    const verificationCode = Object.values(otp).join("");
    if (verificationCode.length !== 6) {
      toast.error(t("enterVerificationCode"));
      return;
    }
    dispatch(
      verify_email({
        email: userInfo.email,
        verificationCode,
      })
    );
  };

  const resendVerification = () => {
    if (countdown > 0) return;
    dispatch(resend_verification({ email: userInfo.email }));
    setCountdown(60);
  };

  return (
    <div>
      {loader && (
        <div className="w-screen h-screen flex justify-center items-center fixed left-0 top-0 bg-[#38303033] z-[999]">
          <FadeLoader />
        </div>
      )}
      <Headers />
      <div className="bg-slate-200 min-h-screen flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-md p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center">
                <AiOutlineMail className="text-purple-500 text-2xl" />
              </div>
            </div>
            <h2 className="text-xl font-bold text-slate-700">
              {t("verifyEmailTitle")}
            </h2>
            <p className="text-slate-500 mt-2">
              {t("verificationSentTo")}{" "}
              <span className="font-medium">{userInfo?.email}</span>
            </p>
          </div>

          <form onSubmit={verifyEmail} className="text-slate-600">
            <div className="flex justify-center gap-2 mb-6">
              {[1, 2, 3, 4, 5, 6].map((digit) => (
                <input
                  key={digit}
                  type="text"
                  name={`digit${digit}`}
                  value={otp[`digit${digit}`]}
                  onChange={otpHandle}
                  className="w-12 h-12 border border-slate-300 rounded-md text-center text-xl focus:border-purple-500 focus:outline-none"
                  maxLength={1}
                  required
                />
              ))}
            </div>

            <button
              type="submit"
              className="px-8 w-full py-2 bg-purple-500 shadow-lg hover:shadow-indigo-500/30 text-white rounded-md mb-4"
              disabled={loader}
            >
              {loader ? t("verifying") : t("verifyEmail")}
            </button>

            <div className="text-center">
              <p className="text-slate-500">
                {t("didntReceiveCode")}{" "}
                <button
                  type="button"
                  onClick={resendVerification}
                  className={`text-purple-500 font-medium ${
                    countdown > 0 ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                  disabled={countdown > 0}
                >
                  {t("resend")} {countdown > 0 ? `(${countdown}s)` : ""}
                </button>
              </p>
            </div>
          </form>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default VerifyEmail;
